import { SerenityCard } from '@components/Cards';
import { CarouselLeftArrowIcon } from '@components/Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '@components/Icons/CarouselRightArrowIcon';
import { RightArrowicon } from '@components/Icons/RightArrowIcon';
import { SerenityIcon } from '@components/Icons/SerenityIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useStationMode } from '@hooks/station';
import { useFavorites } from '@hooks/useFavorites';
import { useSwipeCarousel } from '@hooks/useSwipeCarousel';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Album } from 'src/types';
import { placeHolderData } from '../../../../config';
import { getTrackUrlForLocale } from '../../../../utils/localeUtils';

interface HomeSerenityWellnessProps {
  albums: Album[];
  loading: boolean;
  error: string | null;
  filterByGenre: (
    category: 'music' | 'serenity' | 'books',
    genre: string,
  ) => Album[];
  genres?: string[]; // Genres extracted from featured albums data (no API call)
}

export const HomeSerenityWellness = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  albums: _albums,
  loading: isLoading,
  error: displayError,
  filterByGenre,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  genres: _genres,
}: HomeSerenityWellnessProps) => {
  const { t } = useTranslation();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { setActivePopup } = useCommonState();
  const { toggleFavorite } = useFavorites();
  const { loadAlbumById } = useAlbumData();
  const isStationMode = useStationMode();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Add swipe functionality
  const { containerRef } = useSwipeCarousel({
    threshold: 50,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(scrollLeft > 25);
    setCanScrollRight(scrollLeft < maxScroll - 25);
  };

  // Use all albums without genre filtering
  const filteredItems = useMemo(() => {
    return filterByGenre('serenity', 'All');
  }, [filterByGenre]);

  const playSerenityAlbum = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        // Fetch complete album data from API
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        // Get the first contributor for artist info
        const contributor =
          albumData.contributors && albumData.contributors.length > 0
            ? albumData.contributors[0]
            : null;
        const firstTrack = albumData.tracks[0];

        // Add first track to recent tracks when serenity album is played
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);
        const playlist = {
          currentIndex: 0,
          album: albumData,
        };

        // Store current page as previous page before opening music player
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [
      loadAlbumById,
      setActivePlaylist,
      setActivePopup,
      addRecentTrack,
      preferredLocale,
    ],
  );

  return (
    <div id="serenity-wellness" className="w-full mb-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 font-[NapsterSans] px-4 sm:px-6">
        <div className="flex gap-[6px] sm:gap-[8px] items-center hover-transition">
          <SerenityIcon />
          <Link
            to="/serenity-wellness"
            className="flex gap-[6px] sm:gap-[8px] items-center text-[14px] sm:text-[18px] text-white hover:text-[#fcfcfc] transition-all duration-500 ease-in-out font-bold leading-[140%] cursor-pointer group">
            <span className="sm:inline">{t('home.serenityWellness')}</span>
            <span className="hidden sm:inline">
              <RightArrowicon />
            </span>
          </Link>
        </div>
        <Link
          to="/serenity-wellness"
          className="text-xs sm:text-sm text-white hover:text-[#fcfcfc] hover:underline transition-all duration-300 ease-in-out cursor-pointer">
          {t('home.showAll')}
        </Link>
      </div>

      {/* Initial Loading State */}
      {isLoading && filteredItems.length === 0 && (
        <div className="relative w-full group overflow-hidden px-4 sm:px-6">
          <div className="flex gap-[16px] overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6">
            {[...Array(4)].map((_, index) => (
              <div
                key={`loading-${index}`}
                className={`flex-shrink-0 snap-start ${
                  isStationMode ? 'w-[282px]' : 'w-[371px]'
                }`}>
                <div
                  className={`relative w-full rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] ${
                    isStationMode ? 'h-[395px]' : 'h-[520px]'
                  }`}>
                  {/* Background skeleton */}
                  <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />

                  {/* Badge skeleton */}
                  <div className="absolute top-[16px] left-[16px] z-[100] w-[90px] h-[40px] bg-gray-600 rounded-full"></div>

                  {/* Bottom Section skeleton - No album cover for serenity */}
                  <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                    {/* Album name skeleton */}
                    <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>

                    {/* Contributor info skeleton */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-[56px] h-[56px] rounded-full bg-gray-600 flex-shrink-0"></div>
                        <div className="h-[18px] bg-gray-600 rounded w-32"></div>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="h-[12px] bg-gray-600 rounded w-8"></div>
                        <div className="w-[14px] h-[14px] bg-gray-600 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {displayError && !isLoading && (
        <div className="relative w-full pl-4 sm:pl-6">
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="text-red-400 mb-4">
                <svg
                  className="w-8 h-8 mx-auto mb-2"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-medium">
                  {t('home.failedToLoadSerenity')}
                </p>
                <p className="text-xs text-red-300 mt-1">{displayError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State - No serenity content available */}
      {!displayError && !isLoading && filteredItems.length === 0 && (
        <div
          className={`relative w-full pl-4 sm:pl-6 ${
            isStationMode ? 'h-[395px]' : 'h-[520px]'
          }`}>
          <div className="flex items-center justify-center w-full h-full">
            <div className="text-center text-white/60">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
              <p className="text-lg font-medium mb-2">
                {t('home.noSerenityAvailable')}
              </p>
              <p className="text-sm text-white/40">
                {t('home.noTrendingSerenity')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Slider Content */}
      {!displayError && (
        <div className="relative w-full group px-4 sm:px-6">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const container = containerRef.current || carouselRef.current;
                if (container) {
                  container.scrollBy({
                    left: -400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute left-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.previousSlide')}>
              <CarouselLeftArrowIcon />
            </button>
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const container = containerRef.current || carouselRef.current;
                if (container) {
                  container.scrollBy({
                    left: 400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute right-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.nextSlide')}>
              <CarouselRightArrowIcon />
            </button>
          )}

          <div
            ref={(el) => {
              carouselRef.current = el;
              containerRef.current = el;
            }}
            className="flex gap-[16px] overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6"
            onScroll={handleScroll}>
            {filteredItems.map((album: Album, index: number) => {
              const contributor = album.contributors[0];
              const albumId = album.id || album.albumName || index.toString();
              const isLoading = loadingAlbumId === album.id;
              // Priority for poster image: album.videoMobilePreviewUrl -> contributor?.videoMobilePreviewUrl -> contributor?.thumbnailUrl
              const posterUrl =
                album.videoMobilePreviewUrl ||
                contributor?.videoMobilePreviewUrl ||
                contributor?.thumbnailUrl;

              return (
                <div
                  key={`serenity-${albumId}`}
                  className={`flex-shrink-0 snap-start relative ${
                    isStationMode ? 'w-[282px]' : 'w-[371px]'
                  }`}>
                  <SerenityCard
                    album={album}
                    contributor={contributor}
                    videoUrl={
                      album.videoMobileUrl || contributor?.videoMobileUrl
                    }
                    posterUrl={posterUrl}
                    albumName={album.albumName}
                    likes={album.likes}
                    isLiked={album.isLiked}
                    onClick={() => playSerenityAlbum(album)}
                    onFavoriteClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(
                        'album',
                        album.id,
                        album.isLiked,
                        album.albumName,
                        contributor?.givenName,
                        album.category,
                      );
                    }}
                    showLikesCount={true}
                    compactSize={isStationMode}
                  />
                  {isLoading && (
                    <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <NapsterSpinner />
                    </div>
                  )}
                </div>
              );
            })}

            {/* No infinite scroll on home page - all items loaded at once */}
          </div>
        </div>
      )}
    </div>
  );
};
